import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Eye, Briefcase, UserX, X, Ban, ArrowUpRight } from "lucide-react";
import CandidateDetailModal from "@/components/CandidateDetailModal";

const stages = [
  { k: "recommended", label: "Recomendado" },
  { k: "interview", label: "Entrevista" },
  { k: "selected", label: "Selecionado" },
  { k: "started", label: "Iniciou funções" },
  { k: "validated", label: "Período mínimo (gera prémio)" },
  { k: "rejected", label: "Rejeitado" },
  { k: "withdrawn", label: "Desistiu" },
  { k: "blacklisted", label: "Blacklist (excluído)" },
];

const EXCLUSION_TYPES = [
  { k: "rejected", label: "Rejeitar candidatura", tone: "chip-neutral", desc: "Perfil não avança neste processo." },
  { k: "withdrawn", label: "Desistência", tone: "chip-neutral", desc: "O próprio candidato decidiu desistir." },
  { k: "blacklisted", label: "Blacklist permanente", tone: "chip-terra", desc: "Não deve voltar a ser considerado." },
];

const REASON_PRESETS = {
  rejected: [
    "Perfil não corresponde aos requisitos",
    "Sem disponibilidade para o horário/local",
    "Referências negativas",
    "Não compareceu à entrevista",
  ],
  withdrawn: [
    "Não atendeu contactos",
    "Encontrou outra oportunidade",
    "Motivo pessoal",
    "Não confirmou o interesse",
  ],
  blacklisted: [
    "Comportamento inadequado",
    "Falsificação de dados",
    "Referências gravemente negativas",
    "Ex-colaborador com histórico problemático",
  ],
};

const STAGE_LABELS = Object.fromEntries(stages.map(s => [s.k, s.label]));

function ExcludeModal({ candidate, onClose, onDone }) {
  const [type, setType] = useState("rejected");
  const [preset, setPreset] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const parts = [];
    if (preset) parts.push(preset);
    if (note.trim()) parts.push(note.trim());
    const reason = parts.join(" — ");
    if (!reason) { toast.error("Escolha um motivo predefinido ou escreva uma nota"); return; }
    setBusy(true);
    try {
      await api.patch(`/candidates/${candidate.id}/stage`, {
        stage: type,
        exclusion_category: preset || null,
        exclusion_reason: reason,
      });
      toast.success(type === "blacklisted" ? "Candidato adicionado à blacklist" : "Candidato excluído");
      onDone?.();
      onClose?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4">
      <form onSubmit={submit} className="bg-white rounded-3xl w-full max-w-xl overflow-hidden shadow-xl">
        <div className="p-6 flex items-start justify-between border-b border-[var(--border)]">
          <div>
            <div className="eyebrow">Excluir candidatura</div>
            <h2 className="font-heading text-2xl mt-1">{candidate.name}</h2>
            <div className="text-xs text-[var(--ink-soft)] mt-1">{candidate.email} · {candidate.phone}</div>
          </div>
          <button type="button" onClick={onClose} className="pill-btn pill-ghost !py-2 !px-3" data-testid="close-exclude-modal"><X className="w-4 h-4"/></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <div className="text-sm font-medium mb-2">Tipo de exclusão</div>
            <div className="grid gap-2">
              {EXCLUSION_TYPES.map((t) => (
                <label key={t.k} className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer ${type === t.k ? "border-[var(--forest)] bg-[rgba(45,90,39,0.06)]" : "border-[var(--border)]"}`} data-testid={`exclude-type-${t.k}`}>
                  <input type="radio" name="exclusion-type" value={t.k} checked={type === t.k} onChange={() => { setType(t.k); setPreset(""); }} className="mt-1"/>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{t.label}</span>
                      {t.k === "blacklisted" && <span className="chip chip-terra">Definitivo</span>}
                    </div>
                    <div className="text-xs text-[var(--ink-soft)] mt-0.5">{t.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="text-sm font-medium">Motivo predefinido</label>
            <select value={preset} onChange={(e) => setPreset(e.target.value)} className="mt-1.5 w-full px-3 py-2.5 rounded-xl bg-[var(--muted)] border border-[var(--border)]" data-testid="exclude-preset-select">
              <option value="">— seleccione um motivo —</option>
              {(REASON_PRESETS[type] || []).map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium">Nota adicional <span className="text-xs text-[var(--ink-soft)]">(opcional)</span></label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} className="mt-1.5 w-full px-3 py-2.5 rounded-xl bg-[var(--muted)] border border-[var(--border)] min-h-[80px]" placeholder="Detalhes internos sobre esta exclusão" data-testid="exclude-note-input"/>
          </div>
          {type === "blacklisted" && (
            <div className="rounded-xl bg-[rgba(217,108,74,0.08)] border border-[rgba(217,108,74,0.3)] p-3 text-xs text-[var(--terracotta)]">
              ⚠️ Esta ação marca o candidato como blacklist permanente. Se voltar a submeter candidatura com o mesmo email ou telefone, a candidatura será bloqueada com a nota "já existe candidatura".
            </div>
          )}
        </div>
        <div className="p-6 border-t border-[var(--border)] flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="pill-btn pill-ghost" data-testid="exclude-cancel">Cancelar</button>
          <button disabled={busy} className="pill-btn pill-primary !bg-[var(--terracotta)] !border-[var(--terracotta)]" data-testid="exclude-confirm">
            <Ban className="w-4 h-4"/> {busy ? "A gravar…" : "Confirmar exclusão"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function AdminCandidates() {
  const [items, setItems] = useState([]);
  const [vacancies, setVacancies] = useState({});
  const [detailId, setDetailId] = useState(null);
  const [excludeTarget, setExcludeTarget] = useState(null);
  const [tab, setTab] = useState("active");  // active | excluded | blacklist | all
  const [districtFilter, setDistrictFilter] = useState("all");

  const load = async () => {
    const [c, v] = await Promise.all([
      api.get("/candidates"),
      api.get("/vacancies"),
    ]);
    setItems(c.data);
    const map = {};
    v.data.forEach((x) => { map[x.id] = x; });
    setVacancies(map);
  };

  useEffect(() => { load(); }, []);

  const change = async (c, stage) => {
    // Route exclusion transitions through the modal to capture reason
    if (["rejected", "withdrawn", "blacklisted"].includes(stage)) {
      setExcludeTarget(c);
      return;
    }
    try {
      await api.patch(`/candidates/${c.id}/stage`, { stage });
      toast.success("Estado atualizado");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const isExcluded = (c) => ["rejected", "withdrawn", "blacklisted"].includes(c.stage);

  // Extract the district from a locality string formatted "Freguesia (Distrito)" or just "Distrito"
  const extractDistrict = (loc) => {
    if (!loc) return "";
    const m = String(loc).match(/\(([^)]+)\)\s*$/);
    if (m) return m[1].trim();
    return String(loc).trim();
  };
  const districts = Array.from(new Set(items.map(c => extractDistrict(c.locality)).filter(Boolean))).sort();

  const filtered = items.filter((c) => {
    // Tab filter
    let pass = true;
    if (tab === "active") pass = !isExcluded(c);
    else if (tab === "excluded") pass = c.stage === "rejected" || c.stage === "withdrawn";
    else if (tab === "blacklist") pass = c.stage === "blacklisted";
    if (!pass) return false;
    // District filter
    if (districtFilter !== "all" && extractDistrict(c.locality) !== districtFilter) return false;
    return true;
  });

  const counts = {
    active: items.filter(c => !isExcluded(c)).length,
    excluded: items.filter(c => c.stage === "rejected" || c.stage === "withdrawn").length,
    blacklist: items.filter(c => c.stage === "blacklisted").length,
    all: items.length,
  };

  const TABS = [
    { k: "active", label: "Ativos", desc: "Em fluxo de recrutamento" },
    { k: "excluded", label: "Excluídos", desc: "Rejeitados ou desistiram" },
    { k: "blacklist", label: "Blacklist", desc: "Bloqueados permanentemente" },
    { k: "all", label: "Todos", desc: "Todos os candidatos" },
  ];

  return (
    <div className="space-y-6">
      <div>
        <div className="eyebrow">Fluxo</div>
        <h1 className="font-heading text-5xl mt-2">Candidatos</h1>
        <p className="text-[var(--ink-soft)] mt-1">Clique em "Ficha" para ver o perfil completo, incluindo CV, experiência e referenciador.</p>
      </div>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-wrap gap-2" data-testid="candidates-tabs">
          {TABS.map((t) => {
            const on = tab === t.k;
            return (
              <button
                key={t.k}
                onClick={() => setTab(t.k)}
                className={`px-4 py-2 rounded-2xl border transition text-left ${on ? "bg-[var(--forest)] text-white border-[var(--forest)]" : "bg-white border-[var(--border)] hover:border-[var(--forest)]"}`}
                data-testid={`tab-${t.k}`}
              >
                <div className="flex items-center gap-2">
                  <span className="font-heading text-base">{t.label}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${on ? "bg-white/20" : "bg-[var(--muted)]"}`}>{counts[t.k]}</span>
                </div>
                <div className={`text-[10px] mt-0.5 ${on ? "text-white/70" : "text-[var(--ink-soft)]"}`}>{t.desc}</div>
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-[var(--ink-soft)]">Distrito</label>
          <select
            value={districtFilter}
            onChange={(e) => setDistrictFilter(e.target.value)}
            className="px-3 py-2 rounded-2xl bg-white border border-[var(--border)] text-sm min-w-[180px]"
            data-testid="district-filter"
          >
            <option value="all">Todos ({items.length})</option>
            {districts.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="card-surface overflow-x-auto">
        <table className="w-full text-sm min-w-[960px]">
          <thead>
            <tr className="bg-[var(--muted)] text-left">
              <th className="px-4 py-3">Candidato</th>
              <th className="px-4 py-3">Vaga</th>
              <th className="px-4 py-3">Referenciador</th>
              <th className="px-4 py-3">Origem</th>
              <th className="px-4 py-3">Estado</th>
              <th className="px-4 py-3 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => {
              const vac = vacancies[c.vacancy_id];
              return (
                <tr key={c.id} className="border-t border-[var(--border)] hover:bg-[var(--muted)]/40" data-testid={`candidate-row-${c.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{c.name}</span>
                      {c.stage === "blacklisted" && <span className="chip chip-terra" title={c.exclusion_reason}>Blacklist</span>}
                    </div>
                    <div className="text-xs text-[var(--ink-soft)]">{c.email} · {c.phone}</div>
                    {isExcluded(c) && c.exclusion_reason && (
                      <div className="text-[11px] text-[var(--terracotta)] mt-1 flex items-start gap-1" data-testid={`exclusion-reason-${c.id}`}>
                        <Ban className="w-3 h-3 mt-0.5 flex-shrink-0"/>
                        <span><strong>{STAGE_LABELS[c.stage]}:</strong> {c.exclusion_reason}</span>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {vac ? (
                      <div className="flex items-center gap-1.5">
                        <Briefcase className="w-3 h-3 text-[var(--forest)]"/>
                        <span className="font-medium text-[var(--ink)]">{vac.title}</span>
                      </div>
                    ) : <span className="text-[var(--ink-soft)]">—</span>}
                    {vac && <div className="text-[10px] text-[var(--ink-soft)] mt-0.5">{vac.locality}</div>}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{c.referrer_code}</td>
                  <td className="px-4 py-3"><span className="chip chip-neutral">{c.source}</span></td>
                  <td className="px-4 py-3">
                    <select value={c.stage} onChange={(e) => change(c, e.target.value)} className="px-3 py-1.5 rounded-full bg-[var(--muted)] border border-[var(--border)] text-xs" data-testid={`stage-select-${c.id}`}>
                      {stages.map((s) => <option key={s.k} value={s.k}>{s.label}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={() => setDetailId(c.id)}
                        className="pill-btn pill-ghost !py-1.5 !px-3"
                        data-testid={`view-candidate-${c.id}`}
                        title="Ver ficha"
                      >
                        <Eye className="w-3.5 h-3.5"/> Ficha
                      </button>
                      {!isExcluded(c) && (
                        <button
                          onClick={() => setExcludeTarget(c)}
                          className="pill-btn pill-ghost !py-1.5 !px-3 !text-[var(--terracotta)]"
                          data-testid={`exclude-candidate-${c.id}`}
                          title="Excluir candidato (com motivo)"
                        >
                          <UserX className="w-3.5 h-3.5"/> Excluir
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-[var(--ink-soft)]">Sem candidatos.</td></tr>}
          </tbody>
        </table>
      </div>

      {detailId && <CandidateDetailModal candidateId={detailId} onClose={() => setDetailId(null)} />}
      {excludeTarget && (
        <ExcludeModal
          candidate={excludeTarget}
          onClose={() => setExcludeTarget(null)}
          onDone={load}
        />
      )}
    </div>
  );
}
